import React from "react";
import { Box, Typography, Card, Chip, Divider, Tooltip } from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Percent as PercentIcon,
  BarChart as BarChartIcon,
} from "@mui/icons-material";
import { InvestmentPathDetail } from "../../interfaces/DTO/mislaka_xml_models";
import { FundDataBasic } from "../../interfaces/DTO/FundDataBasic";
import { formatNumberFloor } from "../../utils/namesFormater";
import {
  ManagementFeeDetails,
  PensionFeeExpenseType,
} from "../../interfaces/DTO/mislaka_xml_pension_models";
import { ManagementFeeDisplay } from "./Chips/ManagementFeeDisplay";

interface InvestmentPathStripProps {
  pathDetails: InvestmentPathDetail;
  fundData?: FundDataBasic[];
  management_fee_details: ManagementFeeDetails[];
   showDepositPercentage?: boolean;
}

export const InvestmentPathStrip: React.FC<InvestmentPathStripProps> = ({
  pathDetails,
  fundData,
  management_fee_details,
  showDepositPercentage,
}) => {
  const formatPercentage = (value: number | undefined): string => {
    if (!value) return "לא זמין";
    return `${Math.round(value)}%`;
  };
  // בוחרים את האיבר האחרון במערך
  const latestFundData = fundData?.[fundData.length - 1];

  const yearToDateReturn = latestFundData?.year_to_date_yield || 0;
  const isPositiveReturn = yearToDateReturn >= 0;

  // שליפת כל האובייקטים הייחודיים לפי ערכי דמי ניהול (מסננים 0)
  const savingFeesList = management_fee_details
    .filter(
      (m) =>
        m.expense_type === PensionFeeExpenseType.FROM_SAVINGS &&
        m.management_fee_rate !== 0
    )
    .reduce((unique: ManagementFeeDetails[], current) => {
      const exists = unique.find(
        (u) => u.management_fee_rate === current.management_fee_rate
      );
      if (!exists) unique.push(current);
      return unique;
    }, [] as ManagementFeeDetails[]);

  const depositFeesList = management_fee_details
    .filter(
      (m) =>
        m.expense_type === PensionFeeExpenseType.FROM_DEPOSITS &&
        m.management_fee_rate !== 0
    )
    .reduce((unique: ManagementFeeDetails[], current) => {
      const exists = unique.find(
        (u) => u.management_fee_rate === current.management_fee_rate
      );
      if (!exists) unique.push(current);
      return unique;
    }, [] as ManagementFeeDetails[]);


  const calculateExposurePercentage = (
    exposure: number | undefined,
    total: number
  ): string => {
    if (!exposure || !total) return "לא זמין";
    return formatPercentage((exposure / total) * 100);
  };

  return (
    <Card variant="outlined" sx={{ mb: 2, p: 1, pr: 0 }} dir="rtl">
      <Box display="flex" gap={1} alignItems="center">
       <Box display="flex" flexDirection="column" alignItems="center" gap={0}>
            <ReturnVisual value={yearToDateReturn} />
        {/* אחוז הפקדה, אם רלוונטי */}
           {showDepositPercentage &&
              pathDetails.deposit_percentage !== undefined &&
              pathDetails.deposit_percentage > 0 &&
              pathDetails.deposit_percentage < 101 && (
                <Typography variant="body2" color="textSecondary" textAlign="center">
                  הפקדה: {formatPercentage(pathDetails.deposit_percentage)}
                </Typography>
            )}
          </Box>

        {/* Main Content */}
        <Box flex={1}>
          <Typography variant="h6" component="div">
            {latestFundData?.fund_name || pathDetails.path_name}{" "}
            <Chip label={`#${pathDetails.path_code}`} />
          </Typography>
          {latestFundData?.fund_classification && (
            <Typography variant="subtitle2" color="textSecondary">
              {latestFundData.fund_classification} |{" "}
              {latestFundData.sub_specialization}
            </Typography>
          )}

          <Box display="flex" gap={4} mt={2}>
            <Box>
              <Typography variant="h4" component="div">
                {formatNumberFloor(pathDetails.accumulated_amount)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                צבירה
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              {isPositiveReturn ? (
                <TrendingUpIcon color="success" />
              ) : (
                <TrendingDownIcon color="error" />
              )}
              <Typography
                variant="h6"
                color={isPositiveReturn ? "success.main" : "error.main"}
              >
                <Tooltip title="תשואה שנתית נוכחית מגמלנט">
                  <span dir="ltr">{formatPercentage(yearToDateReturn)}</span>
                </Tooltip>
                /
                <Tooltip title="תשואה שנתית שדווחה מהיצרן לתקופה ">
                  <span dir="ltr">{pathDetails.net_return ? formatPercentage(pathDetails.net_return) : "חסר"}</span>
                </Tooltip>
              </Typography>
              <Typography variant="body2" color="textSecondary">
                תשואה מתחילת השנה
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              <PercentIcon />
              <ManagementFeeDisplay
                feesList={savingFeesList}
                label="דמי ניהול צבירה"
              />
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              <PercentIcon />
              <ManagementFeeDisplay
                feesList={depositFeesList}
                label="דמי ניהול הפקדה"
              />
            </Box>

          </Box>

          <Divider sx={{ my: 2 }} />

          <Box display="flex" gap={1} alignItems="center">
            <BarChartIcon />
            <Typography variant="body2">
              תשואות 3 שנים:{" "}
              {formatPercentage(latestFundData?.yield_trailing_3_yrs)} | 5 שנים:{" "}
              {formatPercentage(latestFundData?.yield_trailing_5_yrs)}
            </Typography>
            <Typography variant="body2" sx={{ mx: 1 }}>
              |
            </Typography>
            {latestFundData?.stock_market_exposure &&
              latestFundData.total_assets && (
                <>
                  <Typography variant="body2">
                    מניות:{" "}
                    {calculateExposurePercentage(
                      latestFundData.stock_market_exposure,
                      latestFundData.total_assets
                    )}
                  </Typography>
                  <Typography variant="body2" sx={{ mx: 0.5 }}>
                    |
                  </Typography>
                </>
              )}
            {latestFundData?.foreign_currency_exposure &&
              latestFundData.total_assets && (
                <>
                  <Typography variant="body2">
                    מט"ח:{" "}
                    {calculateExposurePercentage(
                      latestFundData.foreign_currency_exposure,
                      latestFundData.total_assets
                    )}
                  </Typography>
                  <Typography variant="body2" sx={{ mx: 0.5 }}>
                    |
                  </Typography>
                </>
              )}
            {latestFundData?.foreign_exposure &&
              latestFundData.total_assets && (
                <Typography variant="body2">
                  חו"ל:{" "}
                  {calculateExposurePercentage(
                    latestFundData.foreign_exposure,
                    latestFundData.total_assets
                  )}
                </Typography>
              )}
          </Box>
        </Box>

        {/* Rating Section */}
        <Box textAlign="center" width={80}>
          <Chip
            label={formatPercentage(
              latestFundData?.cumulative_12m_yield
            )}
            color="warning"
          />
          <Typography variant="body2" color="textSecondary" mt={1}>
תשואה 12 חודשים 
         </Typography>
        </Box>
      </Box>
    </Card>
  );
};

const ReturnVisual: React.FC<{ value: number }> = ({ value }) => {
  const height = Math.min(Math.abs(value) * 3, 100);
  const isPositive = value >= 0;

  return (
    <Box
      sx={{
        width: 40,
        height: 96,
        backgroundColor: "grey.100",
        borderRadius: 1,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        p: 0,
      }}
    >
      <Box
        sx={{
          width: 24,
          height: `${height}%`,
          backgroundColor: isPositive ? "green" : "red",
          borderRadius: "4px 4px 0 0",
        }}
      />
    </Box>
  );
};
